'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { HomeLogoLink } from '@/components/home-logo-link';
import { NavbarLogoImages } from '@/components/navbar/logo-images';
import { ArrowUpRight, Menu } from 'lucide-react';
import { ease, heroFollowRevealDelay } from '@/components/shared/motion';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import { NavbarMobileMenuContent } from '@/components/navbar/mobile-menu';
import { Drawer, DrawerTrigger } from '@/components/ui/drawer';
import { buildCompanyContactRows } from '@/sanity/lib/company-contact-rows';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';
import type { MobileMenuContent, NavbarContent } from '@/sanity/lib/navigation';
import type { SiteSettingsContent } from '@/sanity/lib/site-settings';

const navRevealVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: heroFollowRevealDelay,
      staggerChildren: 0.08,
    },
  },
};

const navItemVariants = {
  hidden: { opacity: 0, x: -18 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.65, ease },
  },
};

export default function Nav({
  content,
  mobileMenu,
  company,
}: {
  content?: NavbarContent | null;
  mobileMenu?: MobileMenuContent | null;
  company: SiteSettingsContent;
}) {
  const isCompactTouch = useIsCompactTouch();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasScrolledPastCompactPoint, setHasScrolledPastCompactPoint] = useState(false);
  const navLinks = content?.showLinks === false ? [] : (content?.links ?? []);
  const navCta = content?.showCTA === false ? null : content?.cta;
  const mobileLinks = mobileMenu?.showLinks === false ? [] : (mobileMenu?.links ?? navLinks);
  const mobileCta = mobileMenu?.showCTA === false ? null : (mobileMenu?.cta ?? navCta);
  const compactHeaderCta = (content?.showCTA === false ? null : navCta) ?? mobileCta;
  const mobileContactRows = useMemo(
    () =>
      mobileMenu?.showContactMethods === false
        ? []
        : buildCompanyContactRows(company, mobileMenu?.companyContactMethods ?? undefined),
    [company, mobileMenu?.showContactMethods, mobileMenu?.companyContactMethods],
  );
  const showMobileMenu =
    content?.showMobileMenu !== false &&
    mobileMenu?.show !== false &&
    Boolean(mobileLinks.length || mobileCta || mobileContactRows.length);
  const brandName = content?.companyName ?? content?.logos?.logo?.alt ?? 'Land Corp Group';
  const activeNavRevealVariants = useMemo(
    () =>
      isCompactTouch
        ? { hidden: {}, visible: { transition: { delayChildren: 0.18, staggerChildren: 0.04 } } }
        : navRevealVariants,
    [isCompactTouch],
  );
  const activeLogoVariants = useMemo(
    () =>
      isCompactTouch
        ? {
            hidden: { opacity: 0, x: -6 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease } },
          }
        : navItemVariants,
    [isCompactTouch],
  );
  const activeCtaVariants = useMemo(
    () =>
      isCompactTouch
        ? {
            hidden: { opacity: 0, x: 6 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease } },
          }
        : navItemVariants,
    [isCompactTouch],
  );

  useEffect(() => {
    const updateScrolledState = () => {
      setHasScrolled(window.scrollY > 4);
      setHasScrolledPastCompactPoint(window.scrollY >= 60);
    };

    updateScrolledState();
    window.addEventListener('scroll', updateScrolledState, { passive: true });
    window.addEventListener('resize', updateScrolledState);

    return () => {
      window.removeEventListener('scroll', updateScrolledState);
      window.removeEventListener('resize', updateScrolledState);
    };
  }, []);

  const menuDrawerDirection = isCompactTouch ? 'bottom' : 'right';

  return (
    <nav
      aria-label="On this page"
      className={cn(
        'inset-x-0 top-0 z-50 border-b transition-colors duration-300',
        content?.sticky === false ? 'absolute' : 'fixed',
        hasScrolled ? 'bg-white dark:bg-black' : 'border-transparent bg-transparent',
      )}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={activeNavRevealVariants}
        className={cn(
          'mx-auto flex max-w-7xl items-center gap-2 px-4 transition-[height] duration-300 ease-in-out max-[427px]:gap-1 max-[427px]:px-3 sm:px-6 lg:gap-4 lg:px-8',
          hasScrolledPastCompactPoint ? 'h-14' : 'h-14 xl:h-20',
        )}
      >
        {content?.showLogo !== false ? (
          <motion.div
            variants={activeLogoVariants}
            className="min-w-0 shrink max-[427px]:max-w-[48%]"
          >
            <HomeLogoLink href="/" aria-label={`${brandName} home`} className="block max-w-full">
              <NavbarLogoImages
                logos={content?.logos}
                alt={brandName}
                onSolidBackground={hasScrolled}
              />
            </HomeLogoLink>
          </motion.div>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-visible lg:gap-2 max-xl:hidden">
          {navLinks.map((link) => (
            <motion.div key={link.href} variants={navItemVariants} className="shrink-0">
              <Link
                href={link.href}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors sm:text-[11px] cursor-pointer',
                  hasScrolled
                    ? 'text-foreground hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-foreground border border-transparent hover:border-border transition-all duration-300 ease-in-out'
                    : 'text-white/80 hover:bg-white/10 hover:text-white border border-transparent hover:backdrop-blur-xl hover:border-white/20 transition-all duration-300 ease-in-out',
                )}
                onClick={() =>
                  trackCtaClick({
                    location: 'navbar_link',
                    label: link.label,
                    href: link.href,
                  })
                }
              >
                {link.label}
              </Link>
            </motion.div>
          ))}
        </div>
        <motion.div variants={activeCtaVariants} className="ml-auto flex items-center gap-2">
          {navCta ? (
            <Link
              href={navCta.href}
              className="max-xl:hidden"
              data-lead-form-trigger={navCta.isLeadForm ? 'true' : undefined}
              data-tracking-location="navbar_cta"
              onClick={() =>
                trackCtaClick({
                  location: 'navbar_cta',
                  label: navCta.label,
                  href: navCta.href,
                  isLeadForm: navCta.isLeadForm,
                  variant: 'primary',
                })
              }
            >
              <Button
                variant="outline"
                size="lg"
                className={cn(
                  'group cursor-pointer relative inline-flex items-center gap-1.5 overflow-hidden rounded-full bg-white px-6 h-9 text-xs font-bold text-foreground transition-all duration-300 ease-in-out focus-visible:outline-none uppercase tracking-wider border',
                  hasScrolled
                    ? 'border-transparent bg-[#948B7F] text-white hover:bg-[#BFB7A1] hover:text-white'
                    : 'border-white/20 bg-white/5 text-white backdrop-blur-xl hover:border-[#BFB7A1] hover:bg-[#BFB7A1] hover:text-white',
                )}
              >
                <span>{navCta.label}</span>
                <ArrowUpRight className="size-4 shrink-0 transition-all duration-300 ease-in-out group-hover:rotate-45" />
              </Button>
            </Link>
          ) : null}
          {compactHeaderCta ? (
            <Link
              href={compactHeaderCta.href}
              className="xl:hidden"
              data-lead-form-trigger={compactHeaderCta.isLeadForm ? 'true' : undefined}
              data-tracking-location="navbar_compact_cta"
              onClick={() =>
                trackCtaClick({
                  location: 'navbar_compact_cta',
                  label: compactHeaderCta.label,
                  href: compactHeaderCta.href,
                  isLeadForm: compactHeaderCta.isLeadForm,
                  variant: 'primary',
                })
              }
            >
              <Button
                variant="outline"
                size="lg"
                className={cn(
                  'group cursor-pointer relative inline-flex shrink-0 items-center gap-1 overflow-hidden rounded-full bg-white px-3 min-h-11 h-11 text-[10px] font-bold text-foreground transition-all duration-300 ease-in-out focus-visible:outline-none uppercase tracking-wider border sm:gap-1.5 sm:px-4 sm:text-xs',
                  hasScrolled
                    ? 'border-transparent bg-[#948B7F] text-white hover:bg-[#BFB7A1] hover:text-white'
                    : 'border-white/20 bg-white/5 text-white backdrop-blur-xl hover:border-[#BFB7A1] hover:bg-[#BFB7A1] hover:text-white',
                )}
              >
                <span className="truncate">{compactHeaderCta.label}</span>
                <ArrowUpRight className="size-3.5 shrink-0 transition-all duration-300 ease-in-out group-hover:rotate-45 sm:size-4" />
              </Button>
            </Link>
          ) : null}
          {showMobileMenu ? (
            <Drawer direction={menuDrawerDirection}>
              <DrawerTrigger asChild>
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  aria-label="Open menu"
                  className={cn(
                    'xl:hidden shrink-0 rounded-full transition-all duration-300 ease-in-out cursor-pointer size-11 min-h-11 min-w-11',
                    hasScrolled
                      ? 'bg-[#948B7F] text-white hover:bg-[#BFB7A1]'
                      : 'border border-white/20 bg-white/5 text-white backdrop-blur-xl hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Menu className="size-4" />
                </Button>
              </DrawerTrigger>
              <NavbarMobileMenuContent
                direction={menuDrawerDirection}
                links={mobileLinks}
                mobileContactRows={mobileContactRows}
                mobileCta={mobileCta}
              />
            </Drawer>
          ) : null}
        </motion.div>
      </motion.div>
    </nav>
  );
}
